import React from "react"

import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid,
	TextField,
	Chip,
	Box,
	Stack,
	InputAdornment,
	IconButton,
} from "@mui/material"

import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker"
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider"
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon"

import { Trans } from "@lingui/macro"
import { DateTime } from "luxon"
import Icon from "@mdi/react"
import { mdiPlus } from "@mdi/js"

import { Visit, UpdateVisitRequest, CreateVisitRequest, AssetMatchBrand } from "models"
import { MatchService } from "services/match.service"
import { VisitService } from "services/visit.service"
import { RestResponse } from "@sinossi/mates-react-library"

export interface ScheduleVisitModalProps {
	open: boolean
	visit?: Visit
	assetId?: string
	matchId?: string
	onClose: () => void
	onSubmit?: (visitData: UpdateVisitRequest) => void
	onSuccess?: (message: string) => void
	onError?: (error: any) => void
}

export interface ScheduleVisitModalState {
	dataOra: DateTime | null
	referente: string
	destinationEmails: string[]
	destinationUserIds: string[]
	emailInput: string
	emailError: string
	loading: boolean
}

export class ScheduleVisitModal extends React.Component<ScheduleVisitModalProps, ScheduleVisitModalState> {
	constructor(props: ScheduleVisitModalProps) {
		super(props)
		
		// Gestisce il caso in cui destinationEmail sia "false" dal database
		const emails = this.normalizeEmails(props.visit?.destinationEmail)
		const userIds = this.normalizeUserIds(props.visit?.destinationUserIds)
		
		this.state = {
			dataOra: props.visit ? DateTime.fromJSDate(new Date(props.visit.dataOra)) : null,
			referente: props.visit?.referente || "",
			destinationEmails: emails,
			destinationUserIds: userIds,
			emailInput: "",
			emailError: "",
			loading: false
		}
	}

	private normalizeEmails = (emails: any): string[] => {
		// Se è un array valido, restituiscilo
		if (Array.isArray(emails)) {
			return emails
		}
		// Se è "false" o qualsiasi altro valore non valido, restituisci array vuoto
		return []
	}

	private normalizeUserIds = (userIds: any): string[] => {
		// Se è un array valido, restituiscilo
		if (Array.isArray(userIds)) {
			return userIds
		}
		// Se è "false" o qualsiasi altro valore non valido, restituisci array vuoto
		return []
	}

	componentDidMount() {
		if (this.props.matchId && !this.props.visit) {
			this.loadMatchData()
		}
	}

	componentDidUpdate(prevProps: ScheduleVisitModalProps) {
		if (prevProps.visit !== this.props.visit) {
			// Debug: log per verificare i dati della visita
			console.log("Visit data received:", this.props.visit)
			console.log("Destination emails:", this.props.visit?.destinationEmail)
			
			// Usa i metodi di normalizzazione anche qui
			const emails = this.normalizeEmails(this.props.visit?.destinationEmail)
			const userIds = this.normalizeUserIds(this.props.visit?.destinationUserIds)
			
			this.setState({
				dataOra: this.props.visit ? DateTime.fromJSDate(new Date(this.props.visit.dataOra)) : null,
				referente: this.props.visit?.referente || "",
				destinationEmails: emails,
				destinationUserIds: userIds
			})
		}
		if (prevProps.matchId !== this.props.matchId && this.props.matchId && !this.props.visit) {
			this.loadMatchData()
		}
	}

	private loadMatchData = () => {
		if (!this.props.matchId) return

		this.setState({ loading: true })
		
		MatchService.getMatchById(
			this.props.matchId,
			(matchResp: RestResponse<AssetMatchBrand, any>) => {
				const emails: string[] = []
				const userIds: string[] = []

				let match = matchResp.data 

				if(match == null)
					return;

				// Aggiungi email del referente del brand
				if (match.brand?.representatives && match.brand.representatives.length > 0) {
					const representative = match.brand.representatives[0]
					if (representative.email) {
						emails.push(representative.email)
						userIds.push(representative.id)
					}
				}

				// Aggiungi email del landlord
				if (match.asset?.landlord?.account?.username) {
					emails.push(match.asset.landlord.account.username)
					userIds.push(match.asset.landlord.account.id)
				}

				console.log(match)

				this.setState({
					destinationEmails: emails,
					destinationUserIds: userIds,
					loading: false
				})
			},
			(error: any) => {
				console.error("Errore nel caricamento del match:", error)
				this.setState({ loading: false })
				if (this.props.onError) {
					this.props.onError(error)
				}
			}
		)
	}

	private handleDateTimeChange = (newValue: DateTime | null) => {
		this.setState({ dataOra: newValue })
	}

	private handleReferenteChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		this.setState({ referente: event.target.value })
	}

	private handleEmailInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		this.setState({ emailInput: event.target.value, emailError: "" })
	}

	private validateEmail = (email: string): boolean => {
		const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
		return emailRegex.test(email)
	}

	private handleAddEmail = () => {
		const email = this.state.emailInput.trim()
		
		if (!email) {
			return
		}

		if (!this.validateEmail(email)) {
			this.setState({ emailError: "Email non valida" })
			return
		}

		if (this.state.destinationEmails.includes(email)) {
			this.setState({ emailError: "Email già presente" })
			return
		}

		this.setState({
			destinationEmails: [...this.state.destinationEmails, email],
			emailInput: "",
			emailError: ""
		})
	}

	private handleRemoveEmail = (index: number) => {
		const newEmails = [...this.state.destinationEmails]
		const newUserIds = [...this.state.destinationUserIds]
		
		newEmails.splice(index, 1)
		// Rimuovi anche l'ID corrispondente se esiste
		if (index < newUserIds.length) {
			newUserIds.splice(index, 1)
		}

		this.setState({
			destinationEmails: newEmails,
			destinationUserIds: newUserIds
		})
	}

	private handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
		if (event.key === 'Enter') {
			event.preventDefault()
			this.handleAddEmail()
		}
	}

	private handleSubmit = () => {
		if (!this.state.dataOra) {
			return
		}

		// Debug: log per verificare cosa stiamo inviando
		console.log("Submitting emails:", this.state.destinationEmails)
		console.log("Submitting userIds:", this.state.destinationUserIds)

		if (this.props.onSubmit) {
			// Modalità aggiornamento
			const updateData: UpdateVisitRequest = {
				dataOra: this.state.dataOra.toISO() || undefined,
				referente: this.state.referente || undefined,
				// Invia sempre l'array, anche se vuoto
				destinationEmail: this.state.destinationEmails,
				destinationUserIds: this.state.destinationUserIds
			}
			console.log("Update data being sent:", updateData)
			this.props.onSubmit(updateData)
		} else if (this.props.assetId) {
			// Modalità creazione
			const createData: CreateVisitRequest = {
				assetId: this.props.assetId,
				matchId: this.props.matchId,
				dataOra: this.state.dataOra.toISO() || "",
				referente: this.state.referente || undefined,
				destinationEmail: this.state.destinationEmails,
				destinationUserIds: this.state.destinationUserIds
			}

			VisitService.createVisit(createData)
				.then((response) => {
					if (this.props.onSuccess) {
						this.props.onSuccess("Sopralluogo programmato con successo")
					}
					this.handleClose()
				})
				.catch((error: any) => {
					console.error("Errore nella creazione del sopralluogo:", error)
					if (this.props.onError) {
						this.props.onError(error)
					}
				})
		}
	}

	private handleClose = () => {
		this.props.onClose()
	}

	private isFormValid = (): boolean => {
		return this.state.dataOra !== null
	}

	render() {
		return (
			<Dialog
				open={this.props.open}
				onClose={this.handleClose}
				maxWidth="sm"
				fullWidth
			>
				<DialogTitle>
					<Trans id="schedule-visit-modal.title">
						{this.props.visit ? "Modifica Sopralluogo" : "Programma Sopralluogo"}
					</Trans>
				</DialogTitle>
				<DialogContent>
					<Grid container spacing={2} sx={{ mt: 1 }}>
						<Grid item xs={12}>
							<LocalizationProvider dateAdapter={AdapterLuxon} adapterLocale="it">
								<DateTimePicker
									label="Data e Ora"
									value={this.state.dataOra}
									onChange={this.handleDateTimeChange}
									slotProps={{
										textField: {
											fullWidth: true,
											required: true,
											error: !this.state.dataOra,
											helperText: !this.state.dataOra ? "La data e ora sono obbligatorie" : ""
										}
									}}
									ampm={false}
									format="dd/MM/yyyy HH:mm"
								/>
							</LocalizationProvider>
						</Grid>
						<Grid item xs={12}>
							<TextField
								fullWidth
								label="Referente"
								value={this.state.referente}
								onChange={this.handleReferenteChange}
								placeholder="Nome del referente (opzionale)"
							/>
						</Grid>
						<Grid item xs={12}>
							<Box>
								<TextField
									fullWidth
									label="Invitati al sopralluogo"
									value={this.state.emailInput}
									onChange={this.handleEmailInputChange}
									onKeyPress={this.handleKeyPress}
									placeholder="Inserisci email e premi Invio"
									error={!!this.state.emailError}
									helperText={this.state.emailError}
									InputProps={{
										endAdornment: (
											<InputAdornment position="end">
												<IconButton
													onClick={this.handleAddEmail}
													edge="end"
													disabled={!this.state.emailInput.trim()}
												>
													<Icon path={mdiPlus} size={1} />
												</IconButton>
											</InputAdornment>
										)
									}}
								/>
								{this.state.destinationEmails.length > 0 && (
									<Stack direction="row" spacing={1} sx={{ mt: 2, flexWrap: 'wrap', gap: 1 }}>
										{this.state.destinationEmails.map((email, index) => (
											<Chip
												key={index}
												label={email}
												onDelete={() => this.handleRemoveEmail(index)}
												color="primary"
												variant="outlined"
											/>
										))}
									</Stack>
								)}
							</Box>
						</Grid>
					</Grid>
				</DialogContent>
				<DialogActions>
					<Button onClick={this.handleClose}>
						<Trans id="schedule-visit-modal.cancel">
							Annulla
						</Trans>
					</Button>
					<Button
						onClick={this.handleSubmit}
						variant="contained"
						disabled={!this.isFormValid() || this.state.loading}
					>
						<Trans id="schedule-visit-modal.save">
							{this.props.visit ? "Salva Modifiche" : "Programma"}
						</Trans>
					</Button>
				</DialogActions>
			</Dialog>
		)
	}
}

export default ScheduleVisitModal
